/* Agroley card "Add to cart" (AGRO-098). Module script; plain form posts still work if this fails to load.
   <agroley-quick-add> wraps the form on a product card. On submit it adds the pack through Shopify's cart API,
   tells the theme's cart (header count, drawer) through the same events the product page uses, flies the pack photo
   to the cart icon (the theme's fly-to-cart element, skipped for reduced motion) and shows "Adding..." then "Added". No data is stored; only the cart API of this shop is called. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

class AgroleyQuickAdd extends HTMLElement {
  #busy = false;
  #timer = 0;

  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('button[type="submit"]');
    this.label = this.button?.querySelector('span');
    this.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
    clearTimeout(this.#timer);
  }

  /** Same flying-picture animation as the product page's Add to cart: the pack photo flies from the button to the cart icon. */
  #fly() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    // The picture to fly: the card's photo, the range explorer's current slide, or the feature split's photo.
    const image =
      this.closest('.ag-card')?.querySelector('.ag-card__media img') ||
      this.closest('.ag-range')?.querySelector('.ag-range__layer[data-state="active"] img') ||
      this.closest('.ag-feature')?.querySelector('.ag-feature__image img');
    if (!cartIcon || !image || !this.button) return;
    const flyer = document.createElement('fly-to-cart');
    flyer.classList.add('fly-to-cart--quick');
    flyer.style.setProperty('background-image', `url("${image.currentSrc || image.src}")`);
    flyer.style.setProperty('--start-opacity', '0');
    flyer.source = this.button;
    flyer.destination = cartIcon;
    document.body.appendChild(flyer);
  }

  #say(key) {
    if (!this.button || !this.label) return;
    this.label.textContent = this.button.dataset[key] || this.button.dataset.label;
  }

  #settle(state, keepMs) {
    this.button.dataset.state = state;
    this.#timer = setTimeout(() => {
      delete this.button.dataset.state;
      this.#say('label');
      this.button.disabled = false;
      this.#busy = false;
    }, keepMs);
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy) return;
    this.#busy = true;
    this.button.disabled = true;
    this.#say('labelAdding');
    this.#fly();

    const data = new FormData(this.form);
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    if (sectionIds.length) data.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(data.get('id')), quantity: Number(data.get('quantity')) || 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const cfg = fetchConfig('javascript', { body: data });
      const response = await (
        await fetch(Theme.routes.cart_add_url, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } })
      ).json();
      const cart = await (await fetch(`${Theme.routes.cart_url}.json`)).json();
      const failed = Boolean(response.status);
      if (failed) {
        this.dispatchEvent(
          new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID', detail: { description: response.description, errors: response.errors } })
        );
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'agroley-quick-add',
          sourceId: this.id || 'agroley-quick-add',
          itemCount: Number(data.get('quantity')) || 1,
          sections: failed ? undefined : response.sections,
          didError: failed,
        },
      });
      this.#say(failed ? 'labelError' : 'labelAdded');
      this.#settle(failed ? 'error' : 'added', failed ? 2500 : 1600);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      this.#say('labelError');
      this.#settle('error', 2500);
    }
  };
}

if (!customElements.get('agroley-quick-add')) customElements.define('agroley-quick-add', AgroleyQuickAdd);
